import { REACT_SELECT_FAIL_FALLBACK_OPTIONS } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import { TAdminPanelMainFieldFormType } from "components/ProjectSpecificComponents/Interfaces/AdminInterface"
import {
	INewRequestFormField,
	IRequestTableFormField,
	ISavedRequestFormField,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import {
	TReactSelectOption,
	TReactSelectSelectedOptions,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { observer } from "mobx-react"
import React, { useMemo } from "react"
import Select from "../../Global/atoms/Select"
import createReactSelectOptions from "../molecules/ToolFieldProperties/createReactSelectOptions"
import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"

interface Props {
	valueKey: string | undefined
	isDisabled?: boolean
	onChange?: (selectedOption: TReactSelectSelectedOptions) => void
	type: TAdminPanelMainFieldFormType
}

/**
 * Select component for the main field, e.g. Jama field.
 * Takes store data and presents all available options
 * based on received data about item's fields.
 */
const MainFieldSelect = observer(({ valueKey, isDisabled = false, onChange, type }: Props) => {
	const formStore = useFormStore()
	const [userIdFieldId, userEmailFieldId, userMobFieldId, userDepFieldId, userNameFieldId] = useMemo(() => {
		if (formStore.selectedFormsConfig == null) return []

		let {
			userIdField: { fieldId: userIdFieldId },
			userEmailField: { fieldId: userEmailFieldId },
			userMobField: { fieldId: userMobFieldId },
			userDepField: { fieldId: userDepFieldId },
			userNameField: { fieldId: userNameFieldId },
		} = formStore.selectedFormsConfig

		return [userIdFieldId, userEmailFieldId, userMobFieldId, userDepFieldId, userNameFieldId]
	}, [formStore.selectedFormsConfig])
	// Since newRequestForm defines input fields then there cant be any readonly fields
	const mainFields =
		(type === "newRequestForm"
			? formStore.mainFieldsData?.filter(
					({ readOnly, fieldType }) => !readOnly && fieldType !== "UNSETTABLE" && fieldType !== "USER_UNSETTABLE"
			  )
			: formStore.mainFieldsData) ?? []
			
	// If mainFieldData exists filters all fields which are readOnly
	// And returns react-select picklist
	const mainFieldOptions = useMemo(
		() => createReactSelectOptions(mainFields, "fieldId", "name"),
		[formStore.mainFieldsData]
	)

	const fieldOptions: TReactSelectOption[] = mainFieldOptions.filter((item) => {
		if (formStore.selectedFormsConfig == null) return REACT_SELECT_FAIL_FALLBACK_OPTIONS

		const formData: Partial<
			INewRequestFormField | IRequestTableFormField | ISavedRequestFormField
		>[] = formStore.selectedFormsConfig[type]
		if (type == "newRequestForm") {
			if (
				item.value === userIdFieldId ||
				item.value === userEmailFieldId ||
				item.value === userMobFieldId ||
				item.value === userDepFieldId ||
				item.value === userNameFieldId
			) {
				item.isDisabled = true
			} else {
				item.isDisabled = false
			}
		}

		// If field isnt already used then returns as it is
		// If field is used then add extra property to object isDisabled
		// isDisabled means that this option cant be used again
		if (!formData.find((excluded) => excluded.mainFieldKey === item.value)) return item
		return (item.isDisabled = true)
	})

	return (
		<Select
			valueKey={valueKey}
			options={fieldOptions}
			isDisabled={isDisabled}
			onChange={onChange}
		/>
	)
})

export default MainFieldSelect
